import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, LockKeyhole, MapPin, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API, api, message } from '../lib/api';
import { useAuth } from '../context/Auth';
import { Brand, Btn, Empty, Loading } from '../components/shared';

const unavailable = {
  revoked: 'The parent closed this invitation. Ask them to send you a new one.',
  expired: 'This invitation has expired. Ask the parent for a fresh invitation.',
  accepted: 'This invitation has already been used.'
};

export default function CoachInvite() {
  const { token } = useParams();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const path = `/coach-invite/${token}`;
  const load = useCallback(() => {
    api.get(`/coach-invites/${token}`).then(r => { setInvite(r.data); setError(''); }).catch(e => setError(message(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/coach-invites/${token}/accept`);
      toast.success(`You’re connected with ${r.data.athlete_name}`);
      navigate(`/athletes/${r.data.athlete_id}`, { replace: true });
    } catch (e) { setError(message(e)); setBusy(false); }
  };

  const player = invite?.player;
  return <div className="public-page coach-invite-page passport-refresh" data-testid="coach-invite-page">
    <header className="public-header">
      <Link data-testid="coach-invite-brand" to="/"><Brand /></Link>
      <Link data-testid="coach-invite-signin-link" to={user ? user.role === 'parent' ? '/parent' : '/dashboard' : `/login?next=${encodeURIComponent(path)}`}>{user ? 'My workspace' : 'Sign in'}<ArrowUpRight size={13} style={{ display: 'inline', marginLeft: 5 }} /></Link>
    </header>
    <div className="public-content">
      {error ? <Empty icon={LockKeyhole} title="This invitation is unavailable" text={error} />
        : loading || !invite ? <Loading />
        : invite.status !== 'pending' ? <Empty icon={LockKeyhole} title="This invitation is unavailable" text={unavailable[invite.status] || 'Ask the parent for a new invitation.'} />
        : <>
          <p className="eyebrow" data-testid="coach-invite-eyebrow">A PARENT IS ASKING FOR YOU</p>
          <article className="coach-invite-card" data-testid="coach-invite-card">
            <div className="coach-invite-photo" data-testid="coach-invite-photo">
              {player.has_photo
                ? <img src={`${API}/coach-invites/${token}/photo`} alt={player.name} data-testid="coach-invite-photo-image" />
                : <span data-testid="coach-invite-photo-initials">{player.name.split(' ').map(p => p[0]).slice(0, 2).join('')}</span>}
            </div>
            <div className="coach-invite-details">
              <p className="coach-invite-label" data-testid="coach-invite-label">YOU’VE BEEN INVITED TO COACH</p>
              <h1 className="passport-stone-type" data-testid="coach-invite-player-name">{player.name}</h1>
              <p className="coach-invite-facts" data-testid="coach-invite-player-facts">Age {player.age} · {player.positions.join(' · ')}</p>
              <p className="coach-invite-parent" data-testid="coach-invite-parent"><ShieldCheck size={14} />Invited by {invite.parent_name}</p>
            </div>
          </article>
          {!user ? <>
            <Btn asChild className="public-join" data-testid="coach-invite-signup-button"><Link to={`/login?mode=signup&role=trainer&next=${encodeURIComponent(path)}`}>Create my coach account<ArrowRight /></Link></Btn>
            <p className="public-note" data-testid="coach-invite-signin-note">Already coaching here? <Link to={`/login?next=${encodeURIComponent(path)}`}>Sign in</Link> and this player joins your roster.</p>
          </> : user.role !== 'trainer' ? <>
            <Empty icon={LockKeyhole} title="A coach account is required" text="This invitation is for the player’s coach. Sign in with a trainer account to accept it." />
            <Btn secondary data-testid="coach-invite-switch-account" onClick={logout}>Use another account</Btn>
          </> : <>
            <Btn className="public-join" data-testid="coach-invite-accept-button" disabled={busy} onClick={accept}>{busy ? 'Connecting…' : `Accept & coach ${player.name.split(' ')[0]}`}<ArrowRight /></Btn>
            <p className="public-note" data-testid="coach-invite-accept-note"><MapPin size={12} style={{ display: 'inline', marginRight: 5 }} />The parent stays in control and can end the connection at any time.</p>
          </>}
        </>}
    </div>
  </div>;
}
